/**
 * MapCallout — the icon-badge + heading + body trio shared by every map panel
 * callout (workspace select hint, empty-locations card, drop-pin tool panel,
 * driving-time tool panel, first-run onboarding card).
 *
 * Owns only the callout *content*; the surrounding card (position, width,
 * padding, border) belongs to the caller — the tool panels deliberately have
 * no card of their own because MapToolbox already wraps them in one.
 *
 * The `lg` size is the centered onboarding variant (larger badge, squared
 * rounding, `<h2>` heading). Colours are the design mockup's hex values, which
 * have no Tailwind-theme equivalent in this app yet.
 */
import { cva } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { cn } from '@/libs/utils/cn';

const badgeVariants = cva('grid place-items-center bg-[#f7fafc] text-link', {
  variants: {
    size: {
      sm: 'mb-2.5 size-8 rounded-full',
      lg: 'mx-auto mb-[18px] size-14 rounded-2xl',
    },
  },
  defaultVariants: { size: 'sm' },
});

const titleVariants = cva('m-0 font-bold text-[#0b2936]', {
  variants: {
    size: {
      sm: 'text-[13px]',
      lg: 'text-[19px]',
    },
  },
  defaultVariants: { size: 'sm' },
});

const bodyVariants = cva('text-[#3c5957]', {
  variants: {
    size: {
      sm: 'mt-1.5 text-[12px] leading-[17px]',
      lg: 'mt-[9px] text-[13.5px] leading-5 [text-wrap:pretty]',
    },
  },
  defaultVariants: { size: 'sm' },
});

export interface MapCalloutProps {
  /** Glyph rendered inside the badge — sized by the caller. */
  icon: ReactNode;
  title: string;
  /** Optional body copy. Omitted by panels that only need a header. */
  children?: ReactNode;
  size?: 'sm' | 'lg';
}

export function MapCallout({ icon, title, children, size = 'sm' }: MapCalloutProps) {
  const Title = size === 'lg' ? 'h2' : 'p';

  return (
    <>
      {/* Callers pass an already-`aria-hidden` glyph, so the badge itself
          needs no ARIA of its own. */}
      <div className={badgeVariants({ size })}>{icon}</div>

      {/* With no body copy the heading carries the gap to whatever follows. */}
      <Title className={cn(titleVariants({ size }), children == null && 'mb-3')}>{title}</Title>

      {children != null && <p className={bodyVariants({ size })}>{children}</p>}
    </>
  );
}
